import { z } from "zod";

/** One calendar month in UTC, for example `2026-10`. */
export const periodSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
  .brand<"Period">();
export type Period = z.infer<typeof periodSchema>;

export function periodOf(now: Date): Period {
  return periodSchema.parse(now.toISOString().slice(0, 7));
}

/** The first instant of the period. */
export function periodStart(period: Period): Date {
  return new Date(`${period}-01T00:00:00.000Z`);
}

/** The first instant of the next period. */
export function periodEnd(period: Period): Date {
  const start = periodStart(period);
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
}

export function previousPeriod(period: Period): Period {
  return periodOf(new Date(periodStart(period).getTime() - 1));
}

/** Stripe accepts a Checkout Session that expires from 30 minutes to 24 hours after it is made. */
const SESSION_MAX_MS = 24 * 3_600_000;
/** The session expires this long before the billing cycle anchor, so the anchor is always in the future. */
const SESSION_ANCHOR_GAP_MS = 10 * 60_000;
/** Closer to the next 1st than this, the anchor moves a month on: 30 minutes of session, the gap, and a margin. */
const ANCHOR_MIN_LEAD_MS = 45 * 60_000;

export interface CheckoutTiming {
  /** The first renewal: the 1st of a month, 00:00 UTC. */
  billingCycleAnchor: Date;
  expiresAt: Date;
}

/**
 * The renewal anchor and the session expiry of a Checkout that starts now. The
 * anchor is the 1st of next month, or the 1st of the month after when the
 * next 1st is too close for a session of at least 30 minutes.
 */
export function checkoutTiming(now: Date): CheckoutTiming {
  const next = periodEnd(periodOf(now));
  const anchor = next.getTime() - now.getTime() < ANCHOR_MIN_LEAD_MS ? periodEnd(periodOf(next)) : next;
  return { billingCycleAnchor: anchor, expiresAt: new Date(Math.min(now.getTime() + SESSION_MAX_MS, anchor.getTime() - SESSION_ANCHOR_GAP_MS)) };
}
